import { create } from 'zustand';
import { allPages, pageInfo, setElementProp, type PageKey, type ProjectDoc } from '@cal/engine';
import { ApiError, put } from '../api';

export interface ProjectSummary {
  id: string;
  name: string;
  customerId: string | null;
  customerName: string | null;
  year: number;
  status: string;
  version: number;
  approvedVersion: number | null;
  approvalCurrent: boolean;
  updatedAt: string;
  updatedByName: string | null;
}

export type SaveState = 'saved' | 'unsaved' | 'saving' | 'failed' | 'conflict';
export type Stage = 1 | 2 | 3 | 4;

interface EditorState {
  project: ProjectSummary | null;
  doc: ProjectDoc | null;
  baseVersion: number;
  savedJson: string;
  past: ProjectDoc[];
  future: ProjectDoc[];
  lastKey: string | null;
  lastAt: number;
  page: PageKey;
  selected: string | null;
  scope: 'all' | 'page';
  stage: Stage;
  contentTab: 'photos' | 'captions' | 'events' | 'brand';
  view: 'single' | 'overview';
  zoom: number | 'fit';
  guides: { bleed: boolean; safe: boolean; binding: boolean };
  saveState: SaveState;
  saveError: string | null;
  conflict: { currentVersion: number; updatedBy: string; updatedAt: string } | null;
  lastSavedAt: string | null;
  focusIssue: string | null;
}

const HISTORY = 100;
const GROUP_MS = 800;

export const useEditor = create<EditorState>(() => ({
  project: null,
  doc: null,
  baseVersion: 0,
  savedJson: '',
  past: [],
  future: [],
  lastKey: null,
  lastAt: 0,
  page: 'm01',
  selected: null,
  scope: 'all',
  stage: 1,
  contentTab: 'photos',
  view: 'single',
  zoom: 'fit',
  guides: { bleed: true, safe: true, binding: true },
  saveState: 'saved',
  saveError: null,
  conflict: null,
  lastSavedAt: null,
  focusIssue: null,
}));

/** Keep the current page valid when the page list changes (cover removed, product type switched). */
function validPage(doc: ProjectDoc, page: PageKey): { page?: PageKey; selected?: null } {
  const pages = allPages(doc);
  return pages.some((p) => p.key === page) ? {} : { page: pages[0].key, selected: null };
}

const recoveryKey = (id: string) => `cs-recovery:${id}`;

export function loadProject(project: ProjectSummary, doc: ProjectDoc) {
  const json = JSON.stringify(doc);
  useEditor.setState({
    project, doc, baseVersion: project.version, savedJson: json, past: [], future: [], lastKey: null,
    page: allPages(doc)[0].key, selected: null, contentTab: 'photos', saveState: 'saved', saveError: null, conflict: null, lastSavedAt: project.updatedAt,
  });
}

/** Local copy of unsaved work, used to recover after a crash or closed tab. */
export function readRecovery(projectId: string): { baseVersion: number; at: string; doc: ProjectDoc } | null {
  try {
    const raw = localStorage.getItem(recoveryKey(projectId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}
export function clearRecovery(projectId: string) {
  try { localStorage.removeItem(recoveryKey(projectId)); } catch { /* storage unavailable */ }
}
let recoveryTimer: ReturnType<typeof setTimeout> | undefined;
function writeRecovery() {
  clearTimeout(recoveryTimer);
  recoveryTimer = setTimeout(() => {
    const s = useEditor.getState();
    if (!s.project || !s.doc) return;
    try {
      if (JSON.stringify(s.doc) === s.savedJson) localStorage.removeItem(recoveryKey(s.project.id));
      else localStorage.setItem(recoveryKey(s.project.id), JSON.stringify({ baseVersion: s.baseVersion, at: new Date().toISOString(), doc: s.doc }));
    } catch { /* quota / private mode: recovery unavailable */ }
  }, 300);
}

/**
 * Apply an edit. Edits sharing a `key` within a short interval (e.g. dragging a
 * slider) become one undo step.
 */
export function edit(fn: (d: ProjectDoc) => ProjectDoc, key?: string) {
  const s = useEditor.getState();
  if (!s.doc) return;
  const next = fn(s.doc);
  if (next === s.doc) return;
  const now = Date.now();
  const group = key && key === s.lastKey && now - s.lastAt < GROUP_MS;
  useEditor.setState({
    doc: next,
    ...validPage(next, s.page),
    past: group ? s.past : [...s.past.slice(-HISTORY + 1), s.doc],
    future: [],
    lastKey: key ?? null,
    lastAt: now,
    saveState: s.saveState === 'conflict' ? 'conflict' : 'unsaved',
  });
  writeRecovery();
  scheduleAutosave();
}

export function undo() {
  const s = useEditor.getState();
  if (!s.doc || !s.past.length) return;
  useEditor.setState({ doc: s.past[s.past.length - 1], ...validPage(s.past[s.past.length - 1], s.page), past: s.past.slice(0, -1), future: [s.doc, ...s.future], lastKey: null, saveState: s.saveState === 'conflict' ? 'conflict' : 'unsaved' });
  writeRecovery();
  scheduleAutosave();
}
export function redo() {
  const s = useEditor.getState();
  if (!s.doc || !s.future.length) return;
  useEditor.setState({ doc: s.future[0], ...validPage(s.future[0], s.page), future: s.future.slice(1), past: [...s.past, s.doc], lastKey: null, saveState: s.saveState === 'conflict' ? 'conflict' : 'unsaved' });
  writeRecovery();
  scheduleAutosave();
}

/** Set an element property honouring the "This month / All months" scope. */
export function setProp(elementId: string, path: string[], value: unknown, key?: string) {
  const s = useEditor.getState();
  if (!s.doc) return;
  const info = pageInfo(s.doc, s.page);
  const scope = info.design !== 'month' || s.scope === 'all' ? 'all' : s.page;
  edit((d) => setElementProp(d, scope, info.design, elementId, path, value), key ?? `${elementId}:${path.join('.')}:${scope}`);
}

let autosaveTimer: ReturnType<typeof setTimeout> | undefined;
let saving: Promise<void> | null = null;
const AUTOSAVE_MS = 1500;
function scheduleAutosave() {
  clearTimeout(autosaveTimer);
  autosaveTimer = setTimeout(() => void save('autosave'), AUTOSAVE_MS);
}

export async function save(kind: 'autosave' | 'manual', name?: string, opts: { force?: boolean } = {}): Promise<boolean> {
  clearTimeout(autosaveTimer);
  if (saving) await saving;
  const s = useEditor.getState();
  if (!s.project || !s.doc) return false;
  if (s.saveState === 'conflict' && !opts.force) return false;
  const json = JSON.stringify(s.doc);
  if (kind === 'autosave' && json === s.savedJson) {
    useEditor.setState({ saveState: 'saved' });
    return true;
  }
  const doc = s.doc;
  let ok = false;
  saving = (async () => {
    useEditor.setState({ saveState: 'saving', saveError: null });
    try {
      const r = await put<{ version: number; status: string; savedAt?: string }>(`/api/projects/${s.project!.id}`, { baseVersion: s.baseVersion, doc, kind, name });
      const cur = useEditor.getState();
      const stillSame = JSON.stringify(cur.doc) === json;
      useEditor.setState({
        baseVersion: r.version,
        savedJson: json,
        saveState: stillSame ? 'saved' : 'unsaved',
        lastSavedAt: r.savedAt ?? cur.lastSavedAt,
        project: cur.project ? { ...cur.project, version: r.version, status: r.status, approvalCurrent: cur.project.approvedVersion === r.version } : null,
        conflict: null,
      });
      if (stillSame) clearRecovery(s.project!.id); else scheduleAutosave();
      ok = true;
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        useEditor.setState({ saveState: 'conflict', conflict: { currentVersion: Number(e.data.currentVersion), updatedBy: String(e.data.updatedBy), updatedAt: String(e.data.updatedAt) }, saveError: e.message });
      } else {
        useEditor.setState({ saveState: 'failed', saveError: e instanceof Error ? e.message : String(e) });
        // retry autosave later; changes are also kept in local recovery storage
        if (kind === 'autosave') autosaveTimer = setTimeout(() => void save('autosave'), 10000);
      }
    }
  })();
  await saving;
  saving = null;
  return ok;
}

/** After a conflict, the user explicitly chose to overwrite the newer server revision. */
export async function overwriteAfterConflict() {
  const s = useEditor.getState();
  if (!s.conflict) return;
  useEditor.setState({ baseVersion: s.conflict.currentVersion, conflict: null, saveState: 'unsaved' });
  await save('manual', undefined, { force: true });
}

export function flushPendingSave(): Promise<boolean> {
  const s = useEditor.getState();
  if (s.saveState === 'unsaved' || s.saveState === 'failed') return save('autosave');
  return Promise.resolve(s.saveState === 'saved');
}
